import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import { createCancelTokenSource } from 'common/api'
import { ReportUpliftLeveledItem } from 'common/api/reporting'
import { useAppSelector } from 'common/hooks/redux'
import { fetchCampaigns } from 'features/campaigns/api'
import {
  ProjectSummaryResponse,
  useActiveProjects,
  useGetTriggerMetricsQuery,
} from 'features/homePage/api'

import KPICard from './KPICard'

interface Props {
  accountId: string
}

const calculateTriggerMetric = (
  data: ProjectSummaryResponse,
  metricName: string
) => {
  const results = data?.email_projects?.reduce((acc, currentValue) => {
    if (currentValue[metricName]) {
      acc += currentValue[metricName]
    }
    return acc
  }, 0)

  return results
}

const TriggerKPI = ({ accountId }: Props) => {
  const { dashboardMode } = useAppSelector((state) => state.authStates)
  const source = createCancelTokenSource()

  const { data: activeProjectsData, hasGQLError: activeProjectsError } =
    useActiveProjects(accountId, source, 'AlwaysOn')

  const activeTriggerProjectIds = useMemo(
    () => activeProjectsData ?? [],
    [activeProjectsData]
  )

  const { data: queryData, isError: hasAPIError } = useGetTriggerMetricsQuery(
    accountId,
    dashboardMode,
    activeTriggerProjectIds
  )

  const [results, setResults] = useState({
    totalIncrementalOpens: 0,
    totalIncrementalClicks: 0,
    totalTriggerCampaigns: 0,
    averageOpensUplift: 0,
    averageClicksUplift: 0,
  })

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (queryData) {
      const data = queryData.data.data
      if (!isLoading) {
        setIsLoading(true)
      }
      if ('email_projects' in data && Object.keys(data).length > 0) {
        const foundResults = {
          ...results,
          totalIncrementalOpens: calculateTriggerMetric(
            data,
            'incremental_opens'
          ),
          totalIncrementalClicks: calculateTriggerMetric(
            data,
            'incremental_clicks'
          ),
          totalTriggerCampaigns: calculateTriggerMetric(
            data,
            'number_of_campaigns'
          ),
        }
        setResults(foundResults)
        setIsLoading(false)
      } else if (Array.isArray(data)) {
        fetchCampaigns({
          variables: {
            page: 1,
            pageSize: 10,
            filter: {
              startDate: moment().subtract(12, 'months').toISOString(),
              endDate: moment().toISOString(),
              accountId,
              projectsList: activeTriggerProjectIds,
            },
          },
          source,
        })
          .then((res) => {
            const foundEntry = (data as ReportUpliftLeveledItem[]).find(
              (entry) => entry.period_label
            )
            if (foundEntry) {
              const upliftsObject = foundEntry.values.find(
                (value) => value.name === 'Average'
              )
              if (upliftsObject) {
                const emailOpens = upliftsObject?.values.find(
                  (value) => value.label === 'Email Opens'
                )
                const emailClicks = upliftsObject?.values.find(
                  (value) => value.label === 'Email Clicks'
                )
                const foundResults = {
                  ...results,
                  averageOpensUplift: emailOpens?.value || 0,
                  averageClicksUplift: emailClicks?.value || 0,
                  totalTriggerCampaigns: res.count,
                }
                setResults(foundResults)
                setIsLoading(false)
              }
            }
            setIsLoading(false)
          })
          .catch(() => {
            setIsLoading(false)
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData])

  const totalTriggerProjects = activeTriggerProjectIds
    ? activeTriggerProjectIds.length
    : 0
  const hasError = hasAPIError || activeProjectsError

  return (
    <KPICard
      hasError={hasError}
      isLoading={isLoading}
      totalTriggerProjects={totalTriggerProjects}
      totalIncrementalOpens={results.totalIncrementalOpens}
      totalIncrementalClicks={results.totalIncrementalClicks}
      totalTriggerCampaigns={results.totalTriggerCampaigns}
      averageClicksUplift={results.averageClicksUplift}
      averageOpensUplift={results.averageOpensUplift}
    />
  )
}

export default TriggerKPI
